import { Component, inject, signal, input, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  ClientService,
  ClientPaymentConfiguration,
  ClientPaymentConfigurationDto,
} from '../../../services/client';
import { DropdownItem } from '../../../services/dropdown';
import { ToastService } from '../../../services/toast';

@Component({
  selector: 'app-client-payment-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectModule,
    ButtonModule,
    TagModule,
    InputTextModule,
    InputNumberModule,
    ToggleSwitchModule,
  ],
  templateUrl: './client-payment-config.html',
  styleUrls: ['../clients-shared.css'],
})
export class ClientPaymentConfigTab implements OnInit {
  clientId = input.required<number>();

  private fb = inject(FormBuilder);
  private svc = inject(ClientService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  readonly currencyOptions: DropdownItem[] = [
    { value: 'INR', label: 'INR — Indian Rupee' },
    { value: 'USD', label: 'USD — US Dollar' },
    { value: 'EUR', label: 'EUR — Euro' },
    { value: 'GBP', label: 'GBP — British Pound' },
    { value: 'AED', label: 'AED — UAE Dirham' },
  ];

  loading = signal(false);
  saving = signal(false);
  exists = signal(false); // true once a config row exists for this client
  integrationOptions = signal<DropdownItem[]>([]);

  form: FormGroup = this.fb.group({
    id: [0],
    paymentIntegrationId: [null as number | null, Validators.required],
    paymentLinkExpiryMinutes: [60 as number | null],
    currencyCode: ['INR'],
    allowPartialPayment: [false],
    allowExpiredPayment: [false],
    autoGeneratePaymentLink: [true],
    autoGeneratePolicy: [false],
    paymentDescription: [''],
    paymentSuccessUrl: ['', Validators.pattern(/^https?:\/\/.+/i)],
    paymentFailureUrl: ['', Validators.pattern(/^https?:\/\/.+/i)],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.loadIntegrations();
    this.load();
  }

  private loadIntegrations() {
    this.svc.ddPaymentIntegrations().subscribe((opts) => this.integrationOptions.set(opts));
  }

  load() {
    this.loading.set(true);
    this.svc.pcGetByClient(this.clientId()).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const d: ClientPaymentConfiguration | null = res?.data ?? null;
        if (d && d.id) {
          this.exists.set(true);
          this.patchForm(d);
        } else {
          this.exists.set(false);
          this.resetForm();
        }
      },
      error: () => {
        this.loading.set(false);
        this.exists.set(false);
        this.resetForm();
      },
    });
  }

  private resetForm() {
    this.form.reset({
      id: 0,
      paymentIntegrationId: null,
      paymentLinkExpiryMinutes: 60,
      currencyCode: 'INR',
      allowPartialPayment: false,
      allowExpiredPayment: false,
      autoGeneratePaymentLink: true,
      autoGeneratePolicy: false,
      paymentDescription: '',
      paymentSuccessUrl: '',
      paymentFailureUrl: '',
      isActive: true,
    });
  }

  private patchForm(d: ClientPaymentConfiguration) {
    this.form.reset({
      id: d.id,
      paymentIntegrationId: d.paymentIntegrationId ?? null,
      paymentLinkExpiryMinutes: d.paymentLinkExpiryMinutes ?? null,
      currencyCode: d.currencyCode ?? 'INR',
      allowPartialPayment: !!d.allowPartialPayment,
      allowExpiredPayment: !!d.allowExpiredPayment,
      autoGeneratePaymentLink: !!d.autoGeneratePaymentLink,
      autoGeneratePolicy: !!d.autoGeneratePolicy,
      paymentDescription: d.paymentDescription ?? '',
      paymentSuccessUrl: d.paymentSuccessUrl ?? '',
      paymentFailureUrl: d.paymentFailureUrl ?? '',
      isActive: d.isActive ?? true,
    });
  }

  private buildDto(): ClientPaymentConfigurationDto {
    const v = this.form.getRawValue();
    return {
      id: Number(v.id) || 0,
      clientId: this.clientId(),
      paymentIntegrationId: Number(v.paymentIntegrationId),
      paymentLinkExpiryMinutes:
        v.paymentLinkExpiryMinutes != null ? Number(v.paymentLinkExpiryMinutes) : null,
      currencyCode: this.nz(v.currencyCode),
      allowPartialPayment: !!v.allowPartialPayment,
      allowExpiredPayment: !!v.allowExpiredPayment,
      autoGeneratePaymentLink: !!v.autoGeneratePaymentLink,
      autoGeneratePolicy: !!v.autoGeneratePolicy,
      paymentDescription: this.nz(v.paymentDescription),
      paymentSuccessUrl: this.nz(v.paymentSuccessUrl),
      paymentFailureUrl: this.nz(v.paymentFailureUrl),
      isActive: !!v.isActive,
    } as ClientPaymentConfigurationDto;
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('paymentIntegrationId')?.invalid)
        this.toast.warning('Payment integration is required.');
      else this.toast.warning('Please fix the highlighted fields (URLs must be valid).');
      return;
    }

    this.saving.set(true);
    this.svc.pcSave(this.buildDto()).subscribe({
      next: (res: any) => {
        // Only one config per client — a duplicate Insert returns 409; fall back to Update.
        if (res?.success === false && res?.statusCode === 409) {
          this.retryAsUpdate();
          return;
        }
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save payment configuration');
          return;
        }
        this.toast.success(res?.message || 'Payment configuration saved');
        this.load();
      },
      error: (err: any) => {
        if (err?.status === 409) {
          this.retryAsUpdate();
          return;
        }
        this.saving.set(false);
        this.toast.error(err?.error?.message || 'Failed to save payment configuration');
      },
    });
  }

  /** A config already exists — re-fetch its id and Update instead of Insert. */
  private retryAsUpdate() {
    this.svc.pcGetByClient(this.clientId()).subscribe({
      next: (res: any) => {
        const d: ClientPaymentConfiguration | null = res?.data ?? null;
        if (!d?.id) {
          this.saving.set(false);
          this.toast.error('A payment configuration already exists but could not be loaded.');
          return;
        }
        this.form.get('id')!.setValue(d.id);
        this.exists.set(true);
        this.svc.pcSave(this.buildDto()).subscribe({
          next: (r2: any) => {
            this.saving.set(false);
            if (r2?.success === false) {
              this.toast.error(r2?.message || 'Failed to update payment configuration');
              return;
            }
            this.toast.success(r2?.message || 'Payment configuration saved');
            this.load();
          },
          error: () => {
            this.saving.set(false);
            this.toast.error('Failed to update payment configuration');
          },
        });
      },
      error: () => {
        this.saving.set(false);
        this.toast.error('Failed to update payment configuration');
      },
    });
  }

  async remove() {
    const id = Number(this.form.get('id')!.value);
    if (!id) return;
    const ok = await this.toast.confirm(
      'The payment configuration for this client will be deleted.',
      'Delete configuration?',
      'Delete',
    );
    if (!ok) return;
    this.svc.pcDelete(id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete configuration');
          return;
        }
        this.toast.success(res?.message || 'Configuration deleted');
        this.load();
      },
    });
  }

  private nz(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }
}
